<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';

const props = withDefaults(defineProps<{
  open: boolean;
  minWidth?: number;
  zIndex?: number;
}>(), {
  minWidth: 300,
  zIndex: 500,
});

const width = defineModel<number>('width', { required: true });

const drawer = useTemplateRef<HTMLElement>('drawer');
const resizing = ref(false);

function maxWidth(): number {
  const parentWidth = drawer.value?.parentElement?.clientWidth ?? window.innerWidth;
  return Math.max(parentWidth * 0.6, props.minWidth);
}

function clampWidth(value: number): number {
  return Math.min(Math.max(value, props.minWidth), maxWidth());
}

const effectiveWidth = computed(() => Math.max(width.value, props.minWidth));

function onResizeStart(event: PointerEvent) {
  resizing.value = true;
  (event.target as HTMLElement).setPointerCapture(event.pointerId);
}

function onResizeMove(event: PointerEvent) {
  if (!resizing.value || !drawer.value) return;
  const right = drawer.value.getBoundingClientRect().right;
  width.value = Math.round(clampWidth(right - event.clientX));
}

function onResizeEnd(event: PointerEvent) {
  if (!resizing.value) return;
  resizing.value = false;
  (event.target as HTMLElement).releasePointerCapture(event.pointerId);
}

defineExpose({
  effectiveWidth,
});
</script>

<template>
  <aside ref="drawer" class="map-side-drawer" :class="{ open: props.open, resizing }"
    :style="{ width: `${effectiveWidth}px`, zIndex: props.zIndex }" :aria-hidden="!props.open">
    <div class="drawer-resize-handle" @pointerdown="onResizeStart" @pointermove="onResizeMove"
      @pointerup="onResizeEnd" @pointercancel="onResizeEnd"></div>
    <div class="drawer-content">
      <slot></slot>
    </div>
  </aside>
</template>

<style scoped>
.map-side-drawer {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--p-content-background);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.3s ease, visibility 0s linear 0.3s;
}

.map-side-drawer.open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.3s ease;
}

.map-side-drawer.resizing {
  transition: none;
  user-select: none;
}

.drawer-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.drawer-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 20;
}

.drawer-resize-handle:hover {
  background: var(--p-primary-color);
  opacity: 0.4;
}
</style>
